import HttpClient from "../../../../core/HttpClient";
import { Authentication } from "@leedpulse-monorepo/classes";

/** Couleur du tag : l'onglet ne rejoue pas la sémantique des états des stores. */
export type PublicationSeverity = 'live' | 'pending' | 'action' | 'rejected' | 'neutral';

export interface PublicationRelease {
    /** Version marketing affichée sur le store (`2.3.0`). */
    version: string;
    /** `versionCode` Android / `buildNumber` iOS, `null` quand le store ne le donne pas. */
    build: string | null;
    /** État brut du store, affiché tel quel quand il n'est pas connu. */
    state: string;
    label: string;
    severity: PublicationSeverity;
    /** Android uniquement : `production` ou `internal`. */
    track: string | null;
}

/** Où en est la version minimum forcée par rapport à ce que le store sert. */
export type MinimumVerdict = 'aligned' | 'behind' | 'ahead' | 'unknown';

export interface PlatformPublication {
    /** `false` quand les identifiants du store manquent côté backend. */
    configured: boolean;
    error: string | null;
    releases: PublicationRelease[];
    /** Version servie au public, `null` si on ne la connaît pas. */
    liveVersion: string | null;
    /** `minimum_version_<platform>` en vigueur. */
    minimumVersion: string | null;
    minimumVerdict: MinimumVerdict;
    /** Console interne (Play Console / App Store Connect). */
    consoleUrl: string;
    /** Lien public de téléchargement, celui qu'on envoie à un client. */
    storeUrl: string | null;
}

export interface PublicationStatus {
    ios: PlatformPublication;
    android: PlatformPublication;
    /** Les deux stores servent la même version et aucun minimum n'est déjà au-delà. */
    canRaiseMinimum: boolean;
    /** Icône publiée de l'app, la même sur les deux stores. */
    appIconUrl: string | null;
    fetchedAt: number;
}

const EMPTY_PLATFORM: PlatformPublication = {
    configured: false,
    error: null,
    releases: [],
    liveVersion: null,
    minimumVersion: null,
    minimumVerdict: 'unknown',
    consoleUrl: '',
    storeUrl: null,
};

export const EMPTY_PUBLICATION_STATUS: PublicationStatus = {
    ios: EMPTY_PLATFORM,
    android: EMPTY_PLATFORM,
    canRaiseMinimum: false,
    appIconUrl: null,
    fetchedAt: 0,
};

/** Fetches the publication state of the latest mobile builds on both stores. */
async function getPublicationStatus(auth: Authentication | null): Promise<PublicationStatus> {
    if (!auth) return EMPTY_PUBLICATION_STATUS;

    const response = await HttpClient.get('/leedflow/publication-status', auth.token);

    if (!response || typeof (response as PublicationStatus).fetchedAt !== 'number') {
        throw new Error('Réponse /leedflow/publication-status inattendue : backend non à jour ?');
    }

    return response as PublicationStatus;
}

export default getPublicationStatus;
